

import {
    Component,
    Prop,
    Vue,
} from 'vue-property-decorator';
import {
    ProjectGroup,
    User,
    WeeklyReport,
    WeeklyReportLine,
} from '@/clients/omb-api';
import Loading from '@/components/Loading.vue';
import InputButton from '@/components/input/InputButton.vue';
import InputSelect from '@/components/input/InputSelect.vue';
import clone from 'clone';
import InputDefault from '@/components/input/InputDefault.vue';
import moment from 'moment';
import { Weekday } from '@/assets/ts/enum/weekday';
import WeeklyReportTableLine from '@/components/reports/WeeklyReportTableLine.vue';
import Api from '@/assets/ts/Api';
import ExportReportDialog from '@/components/dialogs/ExportReportDialog.vue';
import Uploader from '@/assets/ts/uploader';
import { ROLE } from '@/assets/ts/enum/role';
import AlertErrorDetails from '@/components/AlertErrorDetails.vue';
import ErrorUtils from '@/assets/ts/error_utils';
import Service from '@/services/Service';
import ConnectionUtils from '@/assets/ts/connection_utils';
import { SaveReportResult } from '@/assets/ts/types/SaveReportResult';
import BaseDialog from './BaseDialog.vue';

@Component({
    computed: {
        ROLE() {
            return ROLE;
        },
    },
    components: {
        AlertErrorDetails,
        ExportReportDialog,
        WeeklyReportTableLine,
        InputDefault,
        InputSelect,
        InputButton,
        Loading,
        BaseDialog,
    },
})
export default class EditWeeklyReportDialog extends Vue {

    private Weekday = Weekday;

    @Prop({ default: null })
    private existing?: WeeklyReport | null;

    @Prop({ required: true })
    private projectGroup!: ProjectGroup;

    @Prop({ required: true })
    private currentUser!: User;

    @Prop({ default: false })
    private readonly!: boolean;

    private ready = false;
    private report: WeeklyReport | null = null;

    private showErrors: boolean = false;
    private errors: { [key: string]: string } = {};
    private generalError: any | null = null;
    private localSaveError = false;
    private remoteSaveError = false;
    private deleting: boolean = false;
    private submitting: boolean = false;

    private title = 'Wochenbericht';

    private lineToRemove: number | null = null;
    private exportDialogVisible = false;
    private deleteDialogVisible = false;

    private autosaveTimeout?: any;
    private lastSave: Date | null = null;

    private uploader?: Uploader;

    private isOnline = false;

    private get isSigned(): boolean {
        return !!this.report?.fileId || !!this.report?.signature;
    }

    private formatDate(date: Date): string {
        const hours = date.getHours() < 10 ? `0${date.getHours()}` : `${date.getHours()}`;
        const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : `${date.getMinutes()}`;

        return `${hours}:${minutes}`;
    }

    private async created() {
        this.isOnline = ConnectionUtils.addConnectionChangeListener((status) => this.isOnline = status);

        try {
            this.uploader = await Uploader.create();
        } catch (e) {
            console.error('failed to create uploader', e);
        }

        if (this.existing) {
            if (this.existing.id) {
                Api.omb.getWeeklyReport(this.existing.id)
                    .then((r) => {
                        this.setReport(r);
                    })
                    .catch((e) => {
                        console.error('failed to fetch remote report', e);

                        Service.reports.getWeeklyReport(this.existing?.id ?? 0)
                            .then((r) => this.setReport(r))
                            .catch((e2) => {
                                console.error('failed to fetch local report', e2);
                            });
                    });
            } else {
                this.setReport(this.existing);
            }
        } else {
            await this.setInitialValues();
            this.setTitle();
            this.ready = true;
        }
    }

    private setReport(report: WeeklyReport): void {
        this.report = clone(report);
        this.correctDateTypes();
        this.setTitle();
        this.ready = true;
    }

    private setTitle(): void {
        if (this.isSigned || this.readonly) {
            this.title = 'Wochenbericht';
        } else if (this.report?.offlineId) {
            this.title = 'Wochenbericht bearbeiten';
        } else {
            this.title = 'Neuen Wochenbericht erstellen';
        }
    }

    private correctDateTypes() {
        if (!this.report) return;
        this.report.start = this.report.start ? new Date(this.report.start) : undefined;
        this.report.end = this.report.end ? new Date(this.report.end) : undefined;
    }

    private async setInitialValues() {
        if (!this.report) this.report = {};

        const now = moment()
            .hour(0)
            .minute(0)
            .second(0);

        this.report.name = this.projectGroup.name;
        this.report.week = `${now.isoWeek()}/${now.year()}`;
        this.report.week = this.report.week.length === 6 ? `0${this.report.week}` : this.report.week;
        this.report.start = now.isoWeekday(1)
            .toDate();
        this.report.end = now.isoWeekday(5)
            .toDate();
        this.report.department = 'Metallbauarbeiten';

        try {
            const currentUser = await Api.omb.getCurrentUser();
            this.report.foreman = `${currentUser.firstName} ${currentUser.name}`;
        } catch (e) {
            console.error('failed to set foreman automatically', e);
        }

        this.$forceUpdate();
    }

    private close() {
        this.$emit('close');
    }

    private async save(remote?: boolean, close?: boolean, callback?: () => void) {
        if (!this.report || !this.projectGroup || !this.projectGroup.id) return;

        this.submitting = true;
        this.generalError = null;
        this.localSaveError = false;
        this.remoteSaveError = false;
        this.showErrors = false;

        this.report.projectGroupId = this.projectGroup.id;

        try {
            const result = remote ? await Service.reports.saveWeeklyReport(this.report) : await Service.reports.saveWeeklyReportLocal(this.report);
            this.handleResultErrors(result.remoteError, result.localError);

            this.submitting = false;
            this.emitUpdate(result.report);
            if (callback) {
                callback();
            } else if (close) {
                this.close();
            }
        } catch (e) {
            console.error(e);
            ErrorUtils.wrapError('Wochenbericht speichern', this.report, e)
                .then((wrapped) => this.generalError = wrapped);
        }
    }

    private handleResultErrors(remoteError?: any, localError?: any) {
        if (!localError) {
            this.lastSave = new Date();
        } else {
            console.error(localError);
            ErrorUtils.wrapError('Wochenbericht speichern', this.report, localError)
                .then((wrapped) => this.generalError = wrapped);
            this.localSaveError = true;
        }

        if (!remoteError) {
            this.lastSave = new Date();
        } else {
            console.error(remoteError);
            ErrorUtils.wrapError('Wochenbericht speichern', this.report, remoteError)
                .then((wrapped) => this.generalError = wrapped);
            this.remoteSaveError = true;
        }
    }

    private getLines(day: Weekday): WeeklyReportLine[] {
        if (!this.report?.lines) return [];
        return this.report.lines.filter((l) => l.day === day);
    }

    private addLine(day: Weekday): void {
        if (!this.report) return;

        this.report.lines = this.report.lines || [];

        if (!this.report.lines.find((l) => l.day === day)) {
            const dayBefore = this.getDayBefore(day);
            const dayBeforeLines = dayBefore ? this.getLines(dayBefore) : undefined;
            if (dayBeforeLines && dayBeforeLines.length) {
                const linesCopy = clone(dayBeforeLines);
                this.report.lines = this.report.lines.concat(linesCopy.map((l) => {
                    l.id = this.getTempLineId();
                    l.day = day;
                    return l;
                }));
            } else {
                this.report.lines.push({
                    id: this.getTempLineId(),
                    day: day,
                });
            }
        } else {
            const lines = this.report.lines.filter((l) => l.day === day);
            const latestLine = lines[lines.length - 1];
            const latestLineCopy = clone(latestLine);
            latestLineCopy.id = this.getTempLineId();
            latestLineCopy.day = day;
            latestLineCopy.names = '';
            this.report.lines.push(latestLineCopy);
        }

        this.onReportChange(true);
        this.$forceUpdate();
    }

    private removeLine(id: number | null | undefined): void {
        if (!this.report) return;

        this.lineToRemove = null;
        if (!this.report.lines) return;

        this.report.lines = this.report.lines.filter((l) => l.id !== id);
        this.onReportChange(true);
        this.$forceUpdate();
    }

    private getTempLineId(): number {
        if (!this.report) return 0;

        let tempId = Math.floor(-999999999 * Math.random());
        // eslint-disable-next-line no-loop-func
        while (this.report.lines && this.report.lines.find((l) => l.id === tempId)) {
            tempId = Math.floor(-999999999 * Math.random());
        }

        return tempId;
    }

    private getDayBefore(day: Weekday): Weekday | undefined {
        switch (day) {
            case Weekday.Tuesday:
                return Weekday.Monday;
            case Weekday.Wednesday:
                return Weekday.Tuesday;
            case Weekday.Thursday:
                return Weekday.Wednesday;
            case Weekday.Friday:
                return Weekday.Thursday;
            default:
                return undefined;
        }
    }

    private signReport(): void {
        this.save(false, false, () => {
            this.deleteDialogVisible = false;
            this.exportDialogVisible = true;
        });
    }

    private async downloadReport(): Promise<void> {
        if (!this.report) return;

        if (!this.uploader) {
            try {
                this.uploader = await Uploader.create();
            } catch (e) {
                console.error('failed to create uploader', e);
            }
        }

        if (!this.uploader || !this.isSigned) return;

        if (!this.report.fileId) {
            // Save remotely to generate document
            this.report = await Api.omb.saveWeeklyReport(this.report);
            // Save normally (remote + local) to persist file id change
            const result = await Service.reports.saveWeeklyReportLocal(this.report);
            if (result.remoteError || result.localError) {
                throw result.remoteError || result.localError;
            }
            this.report = result.report;
        }

        const name = `Wochenbericht_${this.report.name || '?'}_${this.report.week || '?'}`;
        try {
            await this.uploader.download(this.report.fileId || '', 'application/pdf', name);
        } catch (e) {
            ErrorUtils.wrapError('Wochenbericht PDF herunterladen', this.report, e)
                .then((wrapped) => this.generalError = wrapped);
        }
    }

    private showDeleteDialog(): void {
        this.exportDialogVisible = false;
        this.deleteDialogVisible = true;
    }

    private async deleteReport(): Promise<void> {
        if (!this.report) return;

        const toDelete = this.report;
        this.deleting = true;
        this.showErrors = false;

        Service.reports.deleteWeeklyReport(this.report)
            .then(() => {
                this.submitting = false;
                this.$emit('delete', toDelete.id);
                this.close();
            })
            .catch((e) => {
                console.error(e);
                this.deleting = false;
                ErrorUtils.wrapError('Wochenbericht löschen', this.report, e)
                    .then((wrapped) => this.generalError = wrapped);
                this.showErrors = true;
            });
    }

    private emitUpdate(report: WeeklyReport): void {
        this.report = report;
        this.$emit('update', report);
    }

    private onExportSuccess(result: SaveReportResult) {
        if (!result.remoteError || !result.localError) this.emitUpdate(result.report);
        this.handleResultErrors(result.remoteError, result.localError);
    }

    private onReportChange(instantSave?: boolean) {
        if (!this.ready || this.isSigned || this.readonly) return;
        clearTimeout(this.autosaveTimeout);
        this.autosaveTimeout = setTimeout(this.save, instantSave ? 0 : 500);
    }

}
